import { useEffect, useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { Icon } from '../../shared/ui/Icon';
import { formatPrice, getProductImages } from './helpers';
import { LOW_STOCK_THRESHOLD } from '../../shared/config/constants';
import { ProductImage } from './ProductImage';

export function ProductCard({ product }) {
  const { openProduct, addToCart, favorites, toggleFavorite } = useStore();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const title = product.title || product.name || '';
  const isDemoProduct = String(product.id).startsWith('demo-');
  const images = getProductImages(product);
  const price = Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price)
    ? Number(product.salePrice)
    : Number(product.price || 0);
  const oldPrice = Number(product.oldPrice ?? product.price ?? 0);
  const hasDiscount = oldPrice > price;
  const discount = hasDiscount ? Math.round(((oldPrice - price) / oldPrice) * 100) : 0;
  const reviewCount = Number(product.reviewCount || 0);
  const rating = Number(product.rating || 0);
  const stock = Number(product.stock);
  const hasStockData = product.stock !== null && product.stock !== undefined && Number.isFinite(stock);
  const isOutOfStock = hasStockData && stock <= 0;
  const isLowStock = hasStockData && stock > 0 && stock <= LOW_STOCK_THRESHOLD;
  const isFavorite = favorites.includes(String(product.id));

  useEffect(() => setActiveImageIndex(0), [product.id, images.length]);

  const changeImage = (event, offset) => {
    event.stopPropagation();
    setActiveImageIndex((index) => (index + offset + images.length) % images.length);
  };

  return (
    <article className="product-card">
      <div className="product-card-gallery">
        <button className="product-image" onClick={() => openProduct(product)} aria-label={`View ${title}`}>
          {hasDiscount && <span className="sale-tag">-{discount}%</span>}
          {isDemoProduct && <span className="demo-product-tag">DEMO SAMPLE</span>}
          <ProductImage
            src={images[activeImageIndex]}
            alt={`${title}${images.length > 1 ? `, image ${activeImageIndex + 1}` : ''}`}
            title={title}
            category={product.category}
            className="product-card-image"
          />
          <span className="quick-view">Quick view <Icon>↗</Icon></span>
        </button>
        {images.length > 1 && (
          <>
            <button type="button" className="product-image-arrow previous" aria-label={`Previous image of ${title}`} onClick={(event) => changeImage(event, -1)}>
              <Icon>‹</Icon>
            </button>
            <div className="product-image-count" aria-live="polite">{activeImageIndex + 1} / {images.length}</div>
          </>
        )}
        <button
          type="button"
          className={`product-favorite ${isFavorite ? 'is-favorite' : ''}`}
          aria-label={isFavorite ? `Remove ${title} from favorites` : `Save ${title} to favorites`}
          aria-pressed={isFavorite}
          onClick={() => toggleFavorite(product)}
        >
          <Icon>{isFavorite ? '♥' : '♡'}</Icon>
        </button>
      </div>
      <div className="product-meta">
        <span>{product.brand || ''}</span>
        {reviewCount > 0 && rating > 0 && (
          <span className="product-rating"><b>★</b> {rating.toFixed(1)} ({reviewCount})</span>
        )}
      </div>
      <button className="product-title" onClick={() => openProduct(product)}>{title}</button>
      <div className="product-card-stats">
        {hasStockData && (
          <span className={`product-stock ${isOutOfStock ? 'out' : isLowStock ? 'low' : 'available'}`}>
            <i />
            {isOutOfStock ? 'Out of stock' : isLowStock ? `Only ${stock} left` : 'In stock'}
          </span>
        )}
        {hasDiscount && <span className="product-discount">-{discount}%</span>}
      </div>
      {(product.installmentMonthly || product.unitsSold || product.soldCount || product.deliverySpeed) && (
        <div className="product-optional-stats">
          {product.installmentMonthly && <span>From {formatPrice(product.installmentMonthly)}/month</span>}
          {(product.unitsSold || product.soldCount) && <span>{Number(product.unitsSold || product.soldCount).toLocaleString()} sold</span>}
          {product.deliverySpeed && <span>{product.deliverySpeed}</span>}
        </div>
      )}
      <div className="product-bottom">
        <div className="product-card-prices">
          <strong>{formatPrice(price)}</strong>
          {hasDiscount && <del>{formatPrice(oldPrice)}</del>}
        </div>
        <button
          className="add-icon"
          title={isOutOfStock ? 'Out of stock' : 'Add to cart'}
          aria-label={isOutOfStock ? `${title} is out of stock` : `Add ${title} to cart`}
          disabled={isOutOfStock}
          onClick={() => addToCart(product)}
        >
          <Icon>＋</Icon>
        </button>
      </div>
    </article>
  );
}
